import React, { useEffect } from 'react';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function EmailVerificationSuccess() {
  const [authMethod, setAuthMethod] = React.useState<'email' | 'google'>('email');

  useEffect(() => {
    // Set page title
    document.title = 'Authentication Successful - Figgy';
    
    // Check URL params to determine auth method
    const urlParams = new URLSearchParams(window.location.search);
    const hasGoogleParams = urlParams.has('code') || window.location.hash.includes('access_token');
    
    if (hasGoogleParams) {
      setAuthMethod('google');
    }
    
    // Auto-close after 10 seconds if user doesn't interact
    const timer = setTimeout(() => {
      window.close();
    }, 10000);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-lg border border-blue-100">
        {/* Success Icon */}
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        
        {/* Success Message */}
        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          {authMethod === 'google' ? 'Google Sign-In Successful!' : 'Email Verified Successfully!'}
        </h1>
        
        <p className="text-gray-600 mb-6">
          {authMethod === 'google' 
            ? 'You have successfully signed in with Google.' 
            : 'Your email has been verified and you\'re now signed in.'
          }
        </p>
        
        {/* Return Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ArrowLeft className="h-4 w-4 text-blue-600" />
            <span className="text-blue-800 font-medium text-sm">Next Step</span>
          </div>
          <p className="text-blue-700 text-sm">
            Return to your original tab to continue with the onboarding process.
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="space-y-3">
          <Button 
            onClick={() => window.close()}
            className="w-full"
          >
            Close This Tab
          </Button>
          
          <Button 
            variant="outline"
            onClick={() => window.location.href = '/magic-onboarding-v2'}
            className="w-full"
          >
            Continue Onboarding Here Instead
          </Button>
        </div>
        
        {/* Auto-close Notice */}
        <p className="text-xs text-gray-500 mt-4">
          This tab will automatically close in 10 seconds
        </p>
      </div>
    </div>
  );
}